import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, GripVertical, Home, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, apiError, imgUrl } from "../../lib/api";
import { toast } from "sonner";
import { btnDark, btnGhost, btnPrimary, cardCls, inputCls, labelCls } from "./shared";

function SortableItem({ item, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className="relative">
      <span {...attributes} {...listeners} data-testid={`portfolio-drag-${item.id}`}
        className="absolute left-2 top-2 z-10 cursor-grab touch-none rounded-full bg-white/90 p-1.5 shadow"><GripVertical size={14} /></span>
      {children}
    </div>
  );
}

export default function PortfolioTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", collection: "General", show_on_homepage: false, is_active: true });
  const [upForm, setUpForm] = useState({ collection: "General", title: "", description: "", show_on_homepage: false });
  const [newCollection, setNewCollection] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => api.get("/admin/portfolio").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const collections = useMemo(() => [...new Set(items.map((i) => i.collection || "General"))], [items]);
  const displayed = useMemo(() => (filter ? items.filter((i) => (i.collection || "General") === filter) : items), [items, filter]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }));

  const saveOrder = async (ids) => {
    try { await api.post("/admin/portfolio/reorder", { ids }); } catch (err) { toast.error(apiError(err)); }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = items.map((i) => i.id);
    const next = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    setItems(next.map((id) => items.find((i) => i.id === id)));
    saveOrder(next);
  };

  const move = (item, dir) => {
    const ids = items.map((i) => i.id);
    const disp = displayed.map((i) => i.id);
    const a = disp.indexOf(item.id);
    const other = disp[a + dir];
    if (!other) return;
    const ia = ids.indexOf(item.id); const ib = ids.indexOf(other);
    [ids[ia], ids[ib]] = [ids[ib], ids[ia]];
    setItems(ids.map((id) => items.find((i) => i.id === id)));
    saveOrder(ids);
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("collection", newCollection.trim() || upForm.collection);
    fd.append("title", upForm.title);
    fd.append("description", upForm.description);
    fd.append("show_on_homepage", upForm.show_on_homepage);
    try {
      const { data } = await api.post("/admin/portfolio/upload", fd);
      toast.success(`${data.count} portfolio image(s) added`);
      setUpForm({ collection: newCollection.trim() || upForm.collection, title: "", description: "", show_on_homepage: false });
      setNewCollection("");
      load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
    e.target.value = "";
  };

  const openEdit = (it) => {
    setEditing(it.id);
    setForm({ title: it.title || "", description: it.description || "", collection: it.collection || "General", show_on_homepage: !!it.show_on_homepage, is_active: !!it.is_active });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/admin/portfolio/${id}`, form);
      toast.success("Portfolio entry saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const quickToggle = async (it, key) => {
    try {
      await api.put(`/admin/portfolio/${it.id}`, { title: it.title || "", description: it.description || "", collection: it.collection || "General", show_on_homepage: key === "show_on_homepage" ? !it.show_on_homepage : !!it.show_on_homepage, is_active: key === "is_active" ? !it.is_active : !!it.is_active });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this portfolio image permanently?")) return;
    await api.delete(`/admin/portfolio/${id}`).then(() => { toast.success("Deleted"); load(); }).catch((e) => toast.error(apiError(e)));
  };

  const card = (it) => (
    <div className="rounded-2xl bg-white/60 p-3" data-testid={`portfolio-item-${it.id}`}>
      <img src={imgUrl(it.url)} alt={it.title || "Portfolio"} className="h-44 w-full rounded-xl object-cover" />
      <p className="mt-2 truncate text-sm font-light">{it.title || <span className="text-muted-foreground italic">No title</span>}</p>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{it.collection || "General"}</p>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <button data-testid={`portfolio-up-${it.id}`} onClick={() => move(it, -1)} aria-label="Move up" className="rounded-full p-1.5 hover:bg-secondary"><ArrowUp size={13} /></button>
        <button data-testid={`portfolio-down-${it.id}`} onClick={() => move(it, 1)} aria-label="Move down" className="rounded-full p-1.5 hover:bg-secondary"><ArrowDown size={13} /></button>
        <button data-testid={`portfolio-home-${it.id}`} onClick={() => quickToggle(it, "show_on_homepage")} aria-label="Toggle homepage"
          className={`rounded-full p-1.5 hover:bg-secondary ${it.show_on_homepage ? "text-primary" : "text-muted-foreground"}`}><Home size={13} /></button>
        <button data-testid={`portfolio-visibility-${it.id}`} onClick={() => quickToggle(it, "is_active")} aria-label="Toggle visibility"
          className={`rounded-full p-1.5 hover:bg-secondary ${it.is_active ? "" : "text-muted-foreground"}`}>{it.is_active ? <Eye size={13} /> : <EyeOff size={13} />}</button>
        <button data-testid={`portfolio-edit-${it.id}`} onClick={() => openEdit(it)} aria-label="Edit" className="rounded-full p-1.5 hover:bg-secondary"><Pencil size={13} /></button>
        <button data-testid={`portfolio-delete-${it.id}`} onClick={() => remove(it.id)} aria-label="Delete" className="rounded-full p-1.5 hover:bg-secondary hover:text-destructive"><Trash2 size={13} /></button>
      </div>
      {editing === it.id && (
        <div className="mt-3 space-y-2 border-t border-input/60 pt-3" data-testid={`portfolio-edit-form-${it.id}`}>
          <input className={inputCls} data-testid="portfolio-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title" />
          <textarea className={inputCls} data-testid="portfolio-desc-input" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" />
          <input className={inputCls} data-testid="portfolio-collection-input" value={form.collection} onChange={(e) => setForm((f) => ({ ...f, collection: e.target.value }))} placeholder="Collection" list="portfolio-collections" />
          <div className="flex flex-wrap gap-4 text-[11px] uppercase tracking-widest">
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="portfolio-home-check" checked={form.show_on_homepage} onChange={(e) => setForm((f) => ({ ...f, show_on_homepage: e.target.checked }))} className="accent-primary" /> Homepage</label>
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="portfolio-active-check" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} className="accent-primary" /> Published</label>
          </div>
          <div className="flex gap-2">
            <button onClick={() => saveEdit(it.id)} data-testid="portfolio-save-btn" className={btnPrimary}>Save</button>
            <button onClick={() => setEditing(null)} className={btnGhost}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-portfolio-tab">
      <datalist id="portfolio-collections">{collections.map((c) => <option key={c} value={c} />)}</datalist>

      <div className={cardCls}>
        <p className="font-display text-lg">Add portfolio images</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className={labelCls}>Collection
            <select className={inputCls} data-testid="portfolio-collection-select" value={upForm.collection} onChange={(e) => setUpForm((f) => ({ ...f, collection: e.target.value }))}>
              {[...new Set(["General", ...collections])].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className={labelCls}>…or create a new collection
            <input className={inputCls} data-testid="portfolio-new-collection-input" value={newCollection} onChange={(e) => setNewCollection(e.target.value)} placeholder="e.g. Bridal Shoot" /></label>
          <label className={labelCls}>Title (optional)<input className={inputCls} data-testid="portfolio-upload-title" value={upForm.title} onChange={(e) => setUpForm((f) => ({ ...f, title: e.target.value }))} /></label>
          <label className={labelCls}>Description (optional)<input className={inputCls} data-testid="portfolio-upload-desc" value={upForm.description} onChange={(e) => setUpForm((f) => ({ ...f, description: e.target.value }))} /></label>
          <label className="flex items-center gap-2 text-xs uppercase tracking-widest">
            <input type="checkbox" data-testid="portfolio-upload-home" checked={upForm.show_on_homepage} onChange={(e) => setUpForm((f) => ({ ...f, show_on_homepage: e.target.checked }))} className="accent-primary" /> Show on homepage
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="portfolio-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy} className={btnDark}>
            <Plus size={14} /> {busy ? "Uploading…" : "Choose & upload images"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="portfolio-upload-input" onChange={upload} />
        </div>
      </div>

      <div className={cardCls}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button data-testid="portfolio-filter-all" onClick={() => setFilter("")}
            className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest ${!filter ? "bg-foreground text-white" : "border border-input hover:bg-secondary"}`}>All ({items.length})</button>
          {collections.map((c) => (
            <button key={c} data-testid={`portfolio-filter-${c}`} onClick={() => setFilter(c)}
              className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest ${filter === c ? "bg-foreground text-white" : "border border-input hover:bg-secondary"}`}>
              {c} ({items.filter((i) => (i.collection || "General") === c).length})
            </button>
          ))}
        </div>
        <p className="mb-4 text-xs font-light text-muted-foreground">Drag the handle (or use arrows) to reorder. Home icon = appears on homepage. Eye = published on the Portfolio page. Changes go live instantly.</p>
        {displayed.length === 0 ? (
          <p className="rounded-2xl bg-white/60 p-8 text-center text-sm font-light text-muted-foreground" data-testid="portfolio-empty">No portfolio images yet — upload your first set above.</p>
        ) : filter ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{displayed.map(card)}</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((it) => <SortableItem key={it.id} item={it}>{card(it)}</SortableItem>)}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
